import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import CountUp from 'react-countup';

import Iconify from '../../../components/iconify';

const StyledIcon = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  width: theme.spacing(8),
  height: theme.spacing(8),
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
}));

AppSummary.propTypes = {
  color: PropTypes.string,
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  sx: PropTypes.object,
};

export default function AppSummary({ title, total, description, icon, color = 'primary', sx, ...other }) {
  const [isVisible, setIsVisible] = useState(false);
  const aggregateDataRef = useRef(null);

  const handleIntersection = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    });
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(handleIntersection, options);

    const currentRef = aggregateDataRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [aggregateDataRef]);

  return (
    <Box
      ref={aggregateDataRef}
      sx={{
        m: 1,
        px:2,
        py: 5,
        boxShadow: 3,
        textAlign: 'center',
        transition: 'transform 0.3s',
        bgColor: '#34383a',
        borderRadius: 2,
        bgcolor: (theme) => theme.palette.mode === 'light' ? '#F9F9FB' : '#272424',
        // color: (theme) => (theme.palette.mode === 'light' ? theme.palette[color].darker : ''),
        '&:hover': {
          transform: 'scale(1.05)',
          cursor: 'pointer',
        },
        ...sx,
      }}
      {...other}
    >
      <StyledIcon
        sx={{
          color: (theme) => theme.palette[color].dark,
          backgroundImage: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette[color].dark, 0)} 0%, ${alpha(
              theme.palette[color].dark,
              0.24
            )} 100%)`,
        }}
      >
        <Iconify icon={icon} width={24} height={24} />
      </StyledIcon>
      {isVisible && (
        <Typography variant="h3">
          <CountUp end={total} duration={3} separator="," />
        </Typography>
      )}

      <Typography variant="subtitle2" sx={{ opacity: 0.72,  letterSpacing: '0.2em' }}>
        {title}
      </Typography>
      <Typography variant="subtitle4" sx={{ py: 2, my: 2, opacity: 0.72 }}>
        {description}
      </Typography>
    </Box>
  );
}